// src/components/Modal.jsx
import React, { useEffect, useId, useRef } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

let bodyScrollLockCount = 0;
let bodyOverflowBeforeModal = "";
const modalStack = [];

function lockBodyScroll() {
  if (bodyScrollLockCount === 0) {
    bodyOverflowBeforeModal = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }

  bodyScrollLockCount += 1;
}

function unlockBodyScroll() {
  bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);

  if (bodyScrollLockCount === 0) {
    document.body.style.overflow = bodyOverflowBeforeModal;
    bodyOverflowBeforeModal = "";
  }
}

function getFocusableElements(container) {
  if (!container) {
    return [];
  }

  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute("disabled") &&
      element.getAttribute("aria-hidden") !== "true"
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Content = styled.div`
  position: relative;
  width: min(720px, calc(100vw - 32px));
  max-height: calc(100vh - 32px);
  box-sizing: border-box;
  overflow: auto;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  outline: none;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: ${({ theme }) => theme.colors.text};
  border-radius: 10px;
  padding: 6px 10px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

export default function Modal({
  children,
  onClose,
  title,
  role = "dialog",
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  initialFocusRef,
  closeOnEscape = true,
  closeOnOverlay = true,
  restoreFocus = true,
}) {
  const generatedTitleId = useId();
  const contentRef = useRef(null);
  const modalTokenRef = useRef(Symbol("modal"));
  const previouslyFocusedRef = useRef(null);
  const onCloseRef = useRef(onClose);

  onCloseRef.current = onClose;

  const effectiveLabelledBy =
    ariaLabelledBy || (title && !ariaLabel ? generatedTitleId : undefined);

  const effectiveAriaLabel =
    ariaLabel ||
    (!effectiveLabelledBy ? "Fenêtre de dialogue" : undefined);

  useEffect(() => {
    const modalToken = modalTokenRef.current;
    const contentElement = contentRef.current;

    previouslyFocusedRef.current = document.activeElement;
    modalStack.push(modalToken);
    lockBodyScroll();

    const focusInitialElement = () => {
      const requestedElement = initialFocusRef?.current;

      if (
        requestedElement &&
        contentElement?.contains(requestedElement) &&
        typeof requestedElement.focus === "function"
      ) {
        requestedElement.focus();
        return;
      }

      const [firstFocusableElement] = getFocusableElements(contentElement);

      if (firstFocusableElement) {
        firstFocusableElement.focus();
        return;
      }

      contentElement?.focus();
    };

    focusInitialElement();

    const handleKeyDown = (event) => {
      const isTopModal =
        modalStack[modalStack.length - 1] === modalTokenRef.current;

      if (!isTopModal) {
        return;
      }

      if (event.key === "Escape" && closeOnEscape) {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = getFocusableElements(contentRef.current);

      if (focusableElements.length === 0) {
        event.preventDefault();
        contentRef.current?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      const stackIndex = modalStack.lastIndexOf(modalToken);
      if (stackIndex >= 0) {
        modalStack.splice(stackIndex, 1);
      }

      unlockBodyScroll();

      const previouslyFocusedElement = previouslyFocusedRef.current;

      if (
        restoreFocus &&
        previouslyFocusedElement &&
        document.contains(previouslyFocusedElement) &&
        typeof previouslyFocusedElement.focus === "function"
      ) {
        previouslyFocusedElement.focus();
      }
    };
  }, [closeOnEscape, initialFocusRef, restoreFocus]);

  const handleOverlayMouseDown = (event) => {
    if (
      closeOnOverlay &&
      event.target === event.currentTarget &&
      modalStack[modalStack.length - 1] === modalTokenRef.current
    ) {
      onCloseRef.current?.();
    }
  };

  return createPortal(
    <Overlay
      data-testid="modal-overlay"
      onMouseDown={handleOverlayMouseDown}
    >
      <Content
        ref={contentRef}
        role={role}
        aria-modal="true"
        aria-label={effectiveAriaLabel}
        aria-labelledby={effectiveLabelledBy}
        aria-describedby={ariaDescribedBy}
        tabIndex={-1}
      >
        <CloseBtn
          type="button"
          onClick={() => onCloseRef.current?.()}
          aria-label="Fermer"
        >
          ✕
        </CloseBtn>

        {title && (
          <h2 id={generatedTitleId} style={{ marginTop: 0 }}>
            {title}
          </h2>
        )}

        {children}
      </Content>
    </Overlay>,
    document.body
  );
}
